import React, { useRef, useEffect, useState } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Text from "aldoo-ra/Text"
import PostCard from "./post-card"

const RelatedPosts = ({ post, numberOfRelated = 3 }) => {
  const postID = post._id
  const category = post.category?.map((cat) => cat._id) || []
  const scrollContainerRef = useRef(null)
  const isDragging = useRef(false) // Track whether a drag occurred
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    // Set initial value
    setIsMobile(window.innerWidth < 1080)

    // Add resize listener
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1080)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const handleMouseDown = (e) => {
    const container = scrollContainerRef.current
    container.isDragging = true
    container.startX = e.pageX - container.offsetLeft
    container.scrollLeftStart = container.scrollLeft
    container.style.cursor = "grabbing"
    isDragging.current = false
    e.preventDefault()
  }

  const handleMouseMove = (e) => {
    const container = scrollContainerRef.current
    if (!container.isDragging) return
    const x = e.pageX - container.offsetLeft
    const walk = x - container.startX
    container.scrollLeft = container.scrollLeftStart - walk
    if (Math.abs(walk) > 5) {
      isDragging.current = true // Mark as dragging if movement exceeds a small threshold
    }
  }

  const handleMouseUp = () => {
    const container = scrollContainerRef.current
    container.isDragging = false
    container.style.cursor = "grab"
  }

  const handleMouseLeave = () => {
    const container = scrollContainerRef.current
    container.isDragging = false
    container.style.cursor = "grab"
  }

  // Track touch position and direction
  const touchStartX = useRef(0)
  const touchStartY = useRef(0)
  const isHorizontalScroll = useRef(false)
  const initialScrollLeft = useRef(0)

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
    initialScrollLeft.current = scrollContainerRef.current.scrollLeft
    isHorizontalScroll.current = false // Reset on new touch
  }

  const handleTouchMove = (e) => {
    if (!scrollContainerRef.current) return

    const touchX = e.touches[0].clientX
    const touchY = e.touches[0].clientY
    const diffX = touchStartX.current - touchX
    const diffY = touchStartY.current - touchY

    // On first significant movement, determine scroll direction
    if (
      !isHorizontalScroll.current &&
      Math.abs(diffX) > 5 &&
      Math.abs(diffX) > Math.abs(diffY)
    ) {
      isHorizontalScroll.current = true
    }

    // If horizontal scrolling, handle it (without preventDefault)
    if (isHorizontalScroll.current) {
      scrollContainerRef.current.scrollLeft = initialScrollLeft.current + diffX
      // Removed preventDefault() to fix passive event listener error
    }
    // If vertical or undetermined, do nothing and let the page scroll naturally
  }

  const handleTouchEnd = () => {
    isHorizontalScroll.current = false
  }

  // Hide scrollbar styles
  const hideScrollbarStyle = {
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE and Edge
  }

  // Add WebKit scrollbar style
  useEffect(() => {
    // Create style element
    const style = document.createElement("style")
    style.textContent = `
      .no-scrollbar::-webkit-scrollbar {
        display: none;
      }
    `
    // Append to head
    document.head.appendChild(style)

    // Cleanup
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  return (
    <div className="w-full md:px-[56px]">
      <div className="w-full max-w-w1_xl flex flex-col items-center overflow-x-auto mt-[104px] mx-auto">
        <Text
          id="blog_related_posts_title"
          variant="h3"
          className="text-text mb-[30px]"
        />

        {/* For md and below: scrolling enabled */}
        <div
          className="w-full overflow-x-auto cursor-grab md:cursor-default lg:flex lg:gap-4 hide-scrollbar no-scrollbar mx-auto"
          ref={scrollContainerRef}
          style={hideScrollbarStyle}
          onMouseDown={(e) => isMobile && handleMouseDown(e)} // Enable handlers only on md and below
          onMouseMove={(e) => isMobile && handleMouseMove(e)}
          onMouseUp={(e) => isMobile && handleMouseUp(e)}
          onMouseLeave={(e) => isMobile && handleMouseLeave(e)}
          onTouchStart={(e) => isMobile && handleTouchStart(e)}
          onTouchMove={(e) => isMobile && handleTouchMove(e)}
          onTouchEnd={(e) => isMobile && handleTouchEnd(e)}
        >
          <div
            className="flex md:gap-6 snap-x snap-mandatory md:snap-none lg:justify-between lg:w-full no-scrollbar"
            style={{
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
              ...hideScrollbarStyle,
            }}
          >
            <CMSRender
              contentType="BlogPost"
              match={{
                "content.status": "published",
                "content.category._id": { $in: category },
                _id: { $ne: postID },
                $related: numberOfRelated,
              }}
              renderItem={(post) => (
                <div className="snap-start flex-shrink-0 w-[273px] md:w-[345px] lg:w-[32%] lg:max-w-[433px]">
                  <PostCard post={post} />
                </div>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default RelatedPosts
